import { isInBoundsXYZ, parseKeyX, parseKeyY, parseKeyZ, Position, posKeyXYZ, forEachPlaneNeighborKey } from '../voxelMath'

import type { VoxelMap, VoxelData } from '../voxelTypes'
import { e as makeEvent, type EventTuple } from '@/events/types'
import type { Hit } from '../voxelTypes'
import type { ScribbleSettings } from '@/uiState'

export interface SpineEntry {
  voxel: VoxelData
  normal: { x: number; y: number; z: number }
  hitType?: 'voxel' | 'plane'
  surfacePlacement?: 'replaceBoundary'
  mirror?: boolean
  /**
   * The exact pick point (world units), set only when the stroke asks for sub-cell centres
   * (`ScribbleSettings.subcell`); read by the swept brush, never stored in events.
   */
  point?: { x: number; y: number; z: number }
}

export function spineEntry(voxel: VoxelData, hit: Hit): SpineEntry {
  return { voxel, normal: hit.normal, hitType: hit.type, ...(hit.mirror && { mirror: true as const }) }
}

export interface SpinePoint extends SpineEntry {
  key: number
}

export type Spine = SpinePoint[]

// Compute per-step placement centers from a raw spine of hits. This applies the same
// adjacent-on-normal rule used at expansion time, but does not apply any radius-based
// 'onTop' offset. The result is suitable for connection pathing. To avoid re-adjustment
// during expansion, returned entries are marked hitType: 'plane'.
export function placeSpineCenters(
  spine: Spine,
  bounds: { x: number; y: number; z: number },
  base: VoxelMap,
  subtract?: boolean,
): Spine {
  const placed: Spine = []
  for (const entry of spine) {
    const key = entry.key
    const cx = parseKeyX(key)
    const cy = parseKeyY(key)
    const cz = parseKeyZ(key)
    const normal = entry.normal || { x: 0, y: 0, z: 0 }
    const nx = Math.round(normal.x) | 0
    const ny = Math.round(normal.y) | 0
    const nz = Math.round(normal.z) | 0
    const adj = { x: cx + nx, y: cy + ny, z: cz + nz }
    const adjInBounds = isInBoundsXYZ(adj.x, adj.y, adj.z, bounds)
    const adjKey = posKeyXYZ(adj.x, adj.y, adj.z)
    // For draw (not subtract) and grid hits, prefer adjacent along normal when free.
    // This matches original behavior where scribble draws on-top of surfaces.
    const tryAdjacent = !subtract && (entry.hitType === 'voxel')
    const placePos = (tryAdjacent && adjInBounds && !base.has(adjKey)) ? adj : { x: cx, y: cy, z: cz }
    const pk = posKeyXYZ(placePos.x, placePos.y, placePos.z)
    placed.push({ key: pk, voxel: entry.voxel, normal, hitType: 'plane' })
  }
  return placed
}

// Generate a 6-connected path that approximates a straight line between two voxel centers (inclusive)
// Uses a 3D DDA (Amanatides & Woo) style traversal to interleave axis steps smoothly
export function* manhattanPath3D(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }) {
  // Keep name for compatibility, but behavior is a smoother interleaved path
  let x = a.x | 0, y = a.y | 0, z = a.z | 0
  const tx = b.x | 0, ty = b.y | 0, tz = b.z | 0
  yield { x, y, z }
  if (x === tx && y === ty && z === tz) return

  const dx = tx - x, dy = ty - y, dz = tz - z
  const sx = dx === 0 ? 0 : (dx > 0 ? 1 : -1)
  const sy = dy === 0 ? 0 : (dy > 0 ? 1 : -1)
  const sz = dz === 0 ? 0 : (dz > 0 ? 1 : -1)

  const adx = Math.abs(dx), ady = Math.abs(dy), adz = Math.abs(dz)

  // Parametric step size to cross one voxel in each axis
  const invDx = adx === 0 ? Number.POSITIVE_INFINITY : (1 / adx)
  const invDy = ady === 0 ? Number.POSITIVE_INFINITY : (1 / ady)
  const invDz = adz === 0 ? Number.POSITIVE_INFINITY : (1 / adz)

  // Distance (in param t) from center to first boundary in each axis
  let tMaxX = adx === 0 ? Number.POSITIVE_INFINITY : 0.5 * invDx
  let tMaxY = ady === 0 ? Number.POSITIVE_INFINITY : 0.5 * invDy
  let tMaxZ = adz === 0 ? Number.POSITIVE_INFINITY : 0.5 * invDz

  // Param step per voxel boundary crossing
  const tDeltaX = invDx
  const tDeltaY = invDy
  const tDeltaZ = invDz

  while (!(x === tx && y === ty && z === tz)) {
    // Step to the next closest voxel boundary; break ties deterministically X->Y->Z
    if (tMaxX <= tMaxY && tMaxX <= tMaxZ) {
      x += sx
      tMaxX += tDeltaX
    } else if (tMaxY <= tMaxX && tMaxY <= tMaxZ) {
      y += sy
      tMaxY += tDeltaY
    } else {
      z += sz
      tMaxZ += tDeltaZ
    }
    yield { x, y, z }
  }
}

// Build a connected spine Map by inserting 6-connected steps between successive raw points
type AxisLocal = 'x' | 'y' | 'z'

const isSideSupported = (x: number, y: number, z: number, axis: AxisLocal,  bounds: Position, base?: VoxelMap ): boolean => {
  // Check 4-connected neighbors in the plane perpendicular to travel axis
  const key = posKeyXYZ(x, y, z)
  const touchingBound = axis === 'x'
    ? y === 0 || y === bounds.y - 1 || z === 0 || z === bounds.z - 1
    : axis === 'y'
      ? x === 0 || x === bounds.x - 1 || z === 0 || z === bounds.z - 1
      : x === 0 || x === bounds.x - 1 || y === 0 || y === bounds.y - 1

  if (touchingBound) return true

  let supported = false
  forEachPlaneNeighborKey(key, axis, (nk) => {
    if (base?.has(nk)) {
      supported = true
    }
  })
  return supported
}

export function connectSpine(
  spine: Spine,
  bounds: Position,
  opts?: { bridgeDiagonals?: boolean; minRange?: number; maxJump?: number; countUnsupported?: boolean; base?: VoxelMap }
): Spine {
  if (!spine || spine.length === 0) return spine
  const out: Spine = []
  const bridgeDiagonals = opts?.bridgeDiagonals !== false
  const minRange = Math.max(0, Math.floor(opts?.minRange ?? 0))
  const maxJump = opts?.maxJump != null ? Math.max(0, Math.floor(opts.maxJump)) : Number.POSITIVE_INFINITY
  const countUnsupported = !!opts?.countUnsupported


  let prevPoint: SpinePoint | undefined
  for (const point of spine) {
    if (!prevPoint) {
      out.push(point)
      prevPoint = point
      continue
    }
    const px = parseKeyX(prevPoint.key)
    const py = parseKeyY(prevPoint.key)
    const pz = parseKeyZ(prevPoint.key)
    const nx = parseKeyX(point.key)
    const ny = parseKeyY(point.key)
    const nz = parseKeyZ(point.key)
    const ax = Math.abs(nx - px)
    const ay = Math.abs(ny - py)
    const az = Math.abs(nz - pz)
    const isAdjacent = Math.max(ax, ay, az) <= 1
    const nonZeroAxes = (ax ? 1 : 0) + (ay ? 1 : 0) + (az ? 1 : 0)
    const isDiagonalAdjacent = isAdjacent && nonZeroAxes > 1

    let iter: Iterable<{ x: number; y: number; z: number }>
    const manhattan = ax + ay + az
    // Only connect when Manhattan distance is within [minRange, maxJump] (unless counting unsupported)
    if ((minRange > 0 && manhattan < minRange) || (!countUnsupported && manhattan > maxJump)) {
      iter = [{ x: nx, y: ny, z: nz }]
    } else if (!bridgeDiagonals && isDiagonalAdjacent) {
      // Do not insert a corner bridge; keep diagonal as-is
      iter = [{ x: nx, y: ny, z: nz }]
    } else {
      // Optionally compute unsupported steps budget before inserting
      if (countUnsupported && Number.isFinite(maxJump)) {
        // Count unsupported steps on-the-fly; stop early if budget exceeded
        const path: { x: number; y: number; z: number }[] = []
        let unsupported = 0
        let prev: { x: number; y: number; z: number } | undefined
        for (const p of manhattanPath3D({ x: px, y: py, z: pz }, { x: nx, y: ny, z: nz })) {
          path.push(p)
          if (prev) {
        const dx = p.x - prev.x, dy = p.y - prev.y
        const axis: AxisLocal = dx !== 0 ? 'x' : (dy !== 0 ? 'y' : 'z')
        if (!isSideSupported(p.x, p.y, p.z, axis, bounds, opts.base)) {
          unsupported++
          if (unsupported > maxJump) break
        }
          }
          prev = p
        }
        iter = unsupported > maxJump ? [{ x: nx, y: ny, z: nz }] : path
      } else {
        iter = manhattanPath3D({ x: px, y: py, z: pz }, { x: nx, y: ny, z: nz })
      }
    }
    let first = true
    for (const p of iter) {
      const k = posKeyXYZ(p.x, p.y, p.z)
      if (first) { first = false; continue }
      if (k === point.key) continue
      // Mark as already-placed centers so expansion won't shift adjacent again
      out.push({
        key: k,
        voxel: point.voxel,
        normal: prevPoint.normal || point.normal,
        hitType: 'plane',
        surfacePlacement: point.surfacePlacement ?? prevPoint.surfacePlacement,
      })
    }
    out.push(point)
    prevPoint = point
  }
  return out
}

// Build a connected scribble.apply event from a list of points.
export function makeConnectedScribbleEvent(
  ui: any,
  voxel: VoxelData,
  points: { x: number; y: number; z: number }[],
  normal: { x: number; y: number; z: number },
  hitType: 'voxel' | 'plane',
  subtract: boolean,
  opts?: { closed?: boolean }
): EventTuple {
  const closed = !!opts?.closed
  const spine: Spine = []
  for (const p of points) {
    spine.push({ key: posKeyXYZ(p.x, p.y, p.z), voxel, normal, hitType })
  }
  let maxManhattan = 1
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1], b = points[i]
    const d = Math.abs(b.x - a.x) + Math.abs(b.y - a.y) + Math.abs(b.z - a.z)
    if (d > maxManhattan) maxManhattan = d
  }
  const baseScribble: ScribbleSettings = {
    ...(ui?.tool?.scribble ?? {}),
    shouldConnect: true,
    connectMinRange: Math.max(1, maxManhattan),
  }
  const main = makeEvent(['scribble.apply', { spine, scribble: baseScribble, subtract }] as const)
  if (!closed || points.length < 2) return main
  const a = points[points.length - 1]
  const b = points[0]
  if (!a || !b) return main
  const closeSpine: Spine = []
  closeSpine.push({ key: posKeyXYZ(a.x, a.y, a.z), voxel, normal, hitType })
  closeSpine.push({ key: posKeyXYZ(b.x, b.y, b.z), voxel, normal, hitType })
  const d = Math.abs(b.x - a.x) + Math.abs(b.y - a.y) + Math.abs(b.z - a.z)
  const closeScribble: ScribbleSettings = {
    ...baseScribble,
    connectMinRange: Math.max(baseScribble.connectMinRange || 1, d),
  }
  const closeEv = makeEvent(['scribble.apply', { spine: closeSpine, scribble: closeScribble, subtract }] as const)
  return makeEvent(['transaction', [main, closeEv]] as const)
}
